import { Component, inject, OnInit, PLATFORM_ID, ViewChild } from '@angular/core';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { ActivatedRoute, RouterLink } from "@angular/router";
import { SettingsService } from "../../../services/settings/settings.service";
import { AuthLayoutComponent } from '../../ui/auth-layout/auth-layout.component';
import {
  PasswordRuleCheck,
  RESET_PASSWORD_SYMBOLS,
  checkResetPassword,
  resetPasswordPolicyValidator,
} from './password-policy';

/**
 * ControlTower sends some errors as a bare translation key
 * ("error.passwordValidation", "error.http.500"). Those mean nothing to a
 * person, so they count as no message and the copy below takes over.
 */
function readableServerMessage(err: any): string {
  const message = err?.error?.message;
  return typeof message === 'string' && message.trim() && !/^error\./.test(message.trim())
    ? message
    : '';
}

interface ResetFeedback {
  title: string;
  message: string;
  /** Offer "Request a new link" (the token is used up or locked). */
  offerNewLink: boolean;
}

@Component({
  selector: 'app-new-password-form',
  standalone: true,
  imports: [ReactiveFormsModule, RouterLink, AuthLayoutComponent],
  templateUrl: './new-password-form.component.html',
  styleUrl: './new-password-form.component.css'
})
export class NewPasswordFormComponent implements OnInit {
  settingsService = inject(SettingsService);
  route = inject(ActivatedRoute);
  private document = inject(DOCUMENT);
  private platformId = inject(PLATFORM_ID);

  @ViewChild(AuthLayoutComponent) private layout?: AuthLayoutComponent;

  readonly symbols = RESET_PASSWORD_SYMBOLS.split('').join(' ');

  newPasswordForm = new FormGroup({
    newPassword: new FormControl('', [
      Validators.required,
      resetPasswordPolicyValidator
    ]),
    confirmPassword: new FormControl('', [Validators.required])
  }, { validators: this.passwordMatchValidator });

  successful = false;
  showNewPassword = false;
  showConfirmPassword = false;
  /** Which password field Caps Lock was seen on, so the warning sits under it. */
  capsLockField: 'new' | 'confirm' | null = null;
  /** Set on the first submit, so unmet rules turn red only after a try. */
  submitted = false;

  // Token verification
  token: string = '';
  isVerifyingToken: boolean = false;
  isTokenValid: boolean = false;
  tokenError: string = '';
  /** The check itself failed (network/server), so retrying may help. */
  tokenCheckFailed = false;
  maskedEmail: string = '';

  // Feedback
  feedback: ResetFeedback | null = null;
  isLoading: boolean = false;

  ngOnInit() {
    // Extract token from URL query parameter
    this.token = this.route.snapshot.queryParams['token'] || '';

    if (!this.token) {
      this.tokenError = 'This page needs the link from your password reset email. Open the email and select the link, or ask for a new one.';
      return;
    }

    // Verify the token
    this.verifyToken();
  }

  verifyToken() {
    this.isVerifyingToken = true;
    this.tokenError = '';
    this.tokenCheckFailed = false;

    this.settingsService.verifyResetPasswordToken(this.token).subscribe({
      next: (response) => {
        this.isVerifyingToken = false;

        if (response?.valid) {
          this.isTokenValid = true;
          this.maskedEmail = response.email || '';
        } else {
          this.isTokenValid = false;
          this.tokenError = 'Reset links work once and expire after 60 minutes. Ask for a new one and use the newest email.';
        }
        this.layout?.focusHeading();
      },
      error: (err) => {
        this.isVerifyingToken = false;
        this.isTokenValid = false;
        this.tokenCheckFailed = true;
        this.tokenError = err?.status === 0
          ? 'We couldn’t reach the server to check your link. Check your internet connection, then try again.'
          : readableServerMessage(err) || 'We couldn’t check your link. Try again, or ask for a new one.';
        this.layout?.focusHeading();
      }
    });
  }

  // Custom validator to check if passwords match
  passwordMatchValidator(control: AbstractControl): ValidationErrors | null {
    const newPassword = control.get('newPassword');
    const confirmPassword = control.get('confirmPassword');

    if (!newPassword || !confirmPassword) {
      return null;
    }

    return newPassword.value === confirmPassword.value ? null : { passwordMismatch: true };
  }

  // Get form controls for validation
  get newPassword() {
    return this.newPasswordForm.get('newPassword');
  }

  get confirmPassword() {
    return this.newPasswordForm.get('confirmPassword');
  }

  get heading(): string {
    if (this.isVerifyingToken) return 'Checking your link';
    if (this.successful) return 'Password changed';
    if (this.tokenError) {
      if (!this.token) return 'Open the link from your email';
      return this.tokenCheckFailed ? 'We couldn’t check your link' : 'This link no longer works';
    }
    return 'Set a new password';
  }

  get sub(): string {
    if (this.isVerifyingToken) return 'This takes a moment.';
    if (this.successful) return 'Sign in with your new password.';
    if (this.tokenError) return '';
    return this.maskedEmail
      ? `For the account ${this.maskedEmail}.`
      : 'Choose a new password for your account.';
  }

  /** The live rule list under the new password. */
  get rules(): PasswordRuleCheck[] {
    return checkResetPassword(this.newPassword?.value);
  }

  /** Dot colour per rule: unmet rules stay grey until the first submit. */
  ruleTone(rule: PasswordRuleCheck): 'ok' | 'bad' | 'idle' {
    if (rule.state === 'ok') return 'ok';
    if (rule.state === 'bad' || this.submitted) return 'bad';
    return 'idle';
  }

  /**
   * One polite line for screen readers ("4 of 6 rules met"). It changes only
   * when a rule flips, not on every keystroke, and stays empty until typing.
   */
  get rulesSummary(): string {
    if (!this.newPassword?.value) return '';
    const rules = this.rules;
    const met = rules.filter((rule) => rule.state === 'ok').length;
    return met === rules.length
      ? 'All password rules met.'
      : `${met} of ${rules.length} password rules met.`;
  }

  get showNewPasswordError(): boolean {
    const control = this.newPassword;
    return !!control?.invalid && (this.submitted || (control.dirty && control.touched));
  }

  get showConfirmPasswordError(): boolean {
    return this.confirmPasswordErrorMessage !== '';
  }

  // Get error messages for new password
  get newPasswordErrorMessage(): string {
    if (!this.showNewPasswordError) return '';
    if (this.newPassword?.hasError('required')) {
      return 'Enter a new password.';
    }
    if (this.newPassword?.hasError('passwordPolicy')) {
      return 'Meet every rule below to continue.';
    }
    return '';
  }

  // Get error messages for confirm password
  get confirmPasswordErrorMessage(): string {
    const confirm = this.confirmPassword;
    if (!confirm) return '';
    if (confirm.hasError('required')) {
      return this.submitted || (confirm.dirty && confirm.touched) ? 'Type the new password again.' : '';
    }
    if (this.newPasswordForm.hasError('passwordMismatch')) {
      // Wait until they've typed as much as the first field, or left this one.
      const typedEnough = (confirm.value ?? '').length >= (this.newPassword?.value ?? '').length;
      return this.submitted || confirm.touched || typedEnough ? 'The passwords don’t match.' : '';
    }
    return '';
  }

  // Toggle password visibility
  toggleNewPasswordVisibility() {
    this.showNewPassword = !this.showNewPassword;
  }

  toggleConfirmPasswordVisibility() {
    this.showConfirmPassword = !this.showConfirmPassword;
  }

  /** Any key on a password field reports the Caps Lock state for that field. */
  checkCapsLock(event: Event, field: 'new' | 'confirm') {
    const keyboardEvent = event as KeyboardEvent;
    if (typeof keyboardEvent.getModifierState === 'function') {
      this.capsLockField = keyboardEvent.getModifierState('CapsLock') ? field : null;
    }
  }

  clearCapsLock(field: 'new' | 'confirm') {
    if (this.capsLockField === field) this.capsLockField = null;
  }

  // Submit the form
  handleSubmit() {
    if (this.isLoading) return;
    this.submitted = true;

    if (this.newPasswordForm.invalid) {
      this.newPasswordForm.markAllAsTouched();
      this.focusField(this.newPassword?.invalid ? 'new-password' : 'confirm-password');
      return;
    }

    this.isLoading = true;
    this.feedback = null;

    const newPassword = this.newPasswordForm.value.newPassword!;

    this.settingsService.resetUserPassword(this.token, newPassword).subscribe({
      next: () => {
        this.isLoading = false;
        this.successful = true;
        this.newPasswordForm.reset({ newPassword: '', confirmPassword: '' });
        this.layout?.focusHeading();
      },
      error: (err) => {
        this.isLoading = false;
        this.feedback = this.describeError(err);
      }
    });
  }

  private describeError(err: any): ResetFeedback {
    const rawMessage: string = typeof err?.error?.message === 'string' ? err.error.message : '';
    const serverMessage = readableServerMessage(err);

    if (err?.status === 423) {
      return {
        title: 'This link is locked',
        message: 'It was tried 5 times without success. Ask for a new link to try again.',
        offerNewLink: true,
      };
    }
    if (/disabled/i.test(rawMessage)) {
      // 403 "error.accountDisabled"
      return {
        title: 'This account is turned off',
        message: 'Your password wasn’t changed. Ask an administrator in your organization to turn the account back on.',
        offerNewLink: false,
      };
    }
    if (err?.status === 400) {
      // Tested on the raw text: an expired link arrives as "error.invalidToken".
      if (/token|expired|used|invalid link/i.test(rawMessage)) {
        return {
          title: 'This link no longer works',
          message: 'Reset links work once and expire after 60 minutes. Ask for a new one.',
          offerNewLink: true,
        };
      }
      return {
        title: 'The server didn’t accept that password',
        message: (serverMessage || 'It may contain your email or username, or match a recent password.')
          + ' Each try uses one of 5 attempts on this link.',
        offerNewLink: false,
      };
    }
    if (err?.status === 0) {
      return {
        title: 'Can’t reach the server',
        message: 'Your password wasn’t changed. Check your internet connection, then try again.',
        offerNewLink: false,
      };
    }
    return {
      title: 'Your password wasn’t changed',
      message: serverMessage || 'Something went wrong. Try again in a few minutes.',
      offerNewLink: false,
    };
  }

  private focusField(id: string) {
    if (!isPlatformBrowser(this.platformId)) return;
    setTimeout(() => this.document.getElementById(id)?.focus());
  }
}
